/**
 * Main App Component
 * Root application component with routing and global providers
 */

import React from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import 'react-toastify/dist/ReactToastify.css';

import { ThemeCustomizationProvider, useThemeCustomization } from './contexts/ThemeContext';
import { AIAssistantProvider } from './contexts/AIAssistantContext';
import theme from './theme.jsx';
import ProtectedRoute from './components/ProtectedRoute';
import PlaceholderPage from './components/common/PlaceholderPage';
import { FullLayout } from './layouts';
import LoginPage from './modules/auth/LoginPage';
import { resolveBrowserLogoPath } from './config/environment';
import UnauthorizedPage from './pages/UnauthorizedPage';
import HCPSearchPage from "./modules/search/hcp/HCPSearch";
import DrugSearchPage from "./modules/search/drug/DrugSearch";
import InboxPage from './modules/inbox/Inbox';
import SystemInboxPage from './modules/inbox/SystemInbox';
import UserInboxPage from './modules/inbox/UserInbox';
import DrugInboxPage from './modules/inbox/DrugInbox';
import FeedbackInboxPage from './modules/inbox/FeedbackInbox';
import FeedbackTriagePage from './modules/inbox/FeedbackTriage';
import PromptStepsPage from './modules/configuration/PromptSteps';
import PromptRulesPage from './modules/configuration/PromptRules';
import SystemHistoryPage from './modules/history/SystemHistory';
import UserHistoryPage from './modules/history/UserHistory';
import DrugHistoryPage from './modules/history/DrugHistory';
import HistoryRequestDetailsPage from './modules/history/HistoryRequestDetails';
import MarketBasketsPage from './modules/configuration/MarketBaskets';
import BusinessRulesPage from './modules/configuration/BusinessRules';
import MatchRulesPage from './modules/configuration/MatchRules';
import ValidationRulesPage from './modules/configuration/ValidationRules';
import ValidationWorkspacePage from './modules/configuration/ValidationWorkspacePage';
import CleanserWorkspacePage from './modules/configuration/CleanserWorkspacePage';
import HCPCleansersPage from './modules/configuration/HCPCleansers';
import HCPReferenceDatasetsPage from './modules/configuration/HCPReferenceDatasets';
import HCPModelPage from './modules/configuration/HCPModel';
import DrugModelPage from './modules/configuration/DrugModel';
import DrugCleansersPage from './modules/configuration/DrugCleansers';
import DrugReferenceDatasetsPage from './modules/configuration/DrugReferenceDatasets';
import ConfigurationTabsLayout from './modules/configuration/ConfigurationTabsLayout';
import ConfigWorkspacePage from './modules/configuration/ConfigWorkspacePage';
import ConfigCrudWorkspacePage from './modules/configuration/ConfigCrudWorkspacePage';
import ConfigRdmWorkspacePage from './modules/configuration/ConfigRdmWorkspacePage';
import HCPDCRPage from './modules/workflow/HCPDCR';
import HCODCRPage from './modules/workflow/HCODCR';
import RequestDetailsPage from './modules/workflow/RequestDetails';
import HCPMergePage from './modules/workflow/HCPMerge';
import HCPNewRequestPage from './modules/workflow/HCPNewRequest';
import ApproveNdcPage from './modules/workflow/ApproveNdc';
import { DrugMatching as DrugMatchingPage, DrugBrandMap as DrugBrandMapPage } from './modules/matching';
import HCPSrcRecordsPage from './modules/details/HCPSrcRecords';
import HCPDetailsPage from './modules/details/HCPDetails';
import HCODetailsPage from './modules/details/HCODetails';
import DrugDetailsPage from './modules/details/DrugDetails';
import NotFoundPage from './pages/NotFoundPage';
import './App.css';

const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  return null;
};

function App() {
  React.useEffect(() => {
    const logoUrl = resolveBrowserLogoPath();
    if (logoUrl) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/png';
      link.rel = 'shortcut icon';
      link.href = logoUrl;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, []);

  return (
    <ThemeCustomizationProvider>
      <AIAssistantProvider>
        <AppContent />
      </AIAssistantProvider>
    </ThemeCustomizationProvider>
  );
}

const GlobalAuthHandler = () => {
  const navigate = useNavigate();
  
  React.useEffect(() => {
    const handleUnauthorized = () => {
      navigate('/login');
    };
    
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [navigate]);
  
  return null;
};

/**
 * App Content Component
 * Consumes theme customization and applies MUI theme
 */
const AppContent = () => {
  const { customization } = useThemeCustomization();
  const muiTheme = theme(customization);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <ScrollToTop />
      <GlobalAuthHandler />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected Routes with Layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <FullLayout />
            </ProtectedRoute>
          }
        >
          {/* Redirect root to HCP search */}
          <Route index element={<Navigate to="/search/hcp" replace />} />

          {/* Search Routes */}
          <Route path="search/hcp" element={<HCPSearchPage />} />
          <Route path="search/drug" element={<DrugSearchPage />} />

          {/* Other Routes */}
          <Route path="inbox" element={<InboxPage />} />
          <Route path="inbox/system" element={<SystemInboxPage />} />
          <Route path="inbox/user" element={<UserInboxPage />} />
          <Route path="inbox/drug" element={<DrugInboxPage />} />
          <Route path="feedback" element={<FeedbackInboxPage />} />
          <Route path="feedback/:id" element={<FeedbackTriagePage />} />
          <Route path="prompt-rules" element={<PromptStepsPage />} />
          <Route path="prompt-rules/:slotKey" element={<PromptRulesPage />} />
          <Route path="history/system" element={<SystemHistoryPage />} />
          <Route path="history/user" element={<UserHistoryPage />} />
          <Route path="history/drug" element={<DrugHistoryPage />} />
          <Route path="history/request-details/:requestId" element={<HistoryRequestDetailsPage />} />
          <Route path="market-baskets" element={<MarketBasketsPage />} />

          {/* Workflow Routes */}
          <Route path="hcp-dcr" element={<HCPDCRPage />} />
          <Route path="hco-dcr" element={<HCODCRPage />} />
          <Route path="request-details/:requestId" element={<RequestDetailsPage />} />
          <Route path="hcp-merge" element={<HCPMergePage />} />
          <Route path="hcp-new-request" element={<HCPNewRequestPage />} />
          <Route path="approve-ndc" element={<ApproveNdcPage />} />
          <Route path="drug-matching" element={<DrugMatchingPage />} />
          <Route path="drug-brand-map" element={<DrugBrandMapPage />} />
          <Route path="details/hcp/:hcpId" element={<HCPDetailsPage />} />
          <Route path="details/hco/:hcoId" element={<HCODetailsPage />} />
          <Route path="details/drug/:drugId" element={<DrugDetailsPage />} />
          <Route path="details/hcp/:hcpId/source-records" element={<HCPSrcRecordsPage />} />

          {/* Configuration - Admin only */}
          <Route
            path="configuration"
            element={
              <ProtectedRoute requiredPermission="admin">
                <Navigate to="/configuration/hcp/model" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="configuration/hcp"
            element={
              <ProtectedRoute requiredPermission="admin">
                <ConfigurationTabsLayout basePath="/configuration/hcp" entityLabel="HCP" />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="model" replace />} />
            <Route path="model" element={<HCPModelPage />} />
            <Route path="match-rules" element={<MatchRulesPage />} />
            <Route path="business-rules" element={<BusinessRulesPage />} />
            <Route path="validation-rules" element={<ValidationRulesPage />} />
            <Route path="cleansers" element={<HCPCleansersPage />} />
            <Route path="reference-datasets" element={<HCPReferenceDatasetsPage />} />
            <Route path="reference-datasets/workspace/:mode" element={<ConfigRdmWorkspacePage entityType="hcp" />} />
            <Route path="reference-datasets/workspace/:mode/:itemKey" element={<ConfigRdmWorkspacePage entityType="hcp" />} />
            <Route path="validation-rules/workspace/:mode" element={<ValidationWorkspacePage entityType="hcp" />} />
            <Route path="validation-rules/workspace/:mode/:itemKey" element={<ValidationWorkspacePage entityType="hcp" />} />
            <Route path="cleansers/workspace/:mode" element={<CleanserWorkspacePage entityType="hcp" />} />
            <Route path="cleansers/workspace/:mode/:itemKey" element={<CleanserWorkspacePage entityType="hcp" />} />
            <Route path=":section/workspace/:mode" element={<ConfigCrudWorkspacePage entityType="hcp" />} />
            <Route path=":section/workspace/:mode/:itemKey" element={<ConfigCrudWorkspacePage entityType="hcp" />} />
            <Route path="create-business-rules" element={<ConfigWorkspacePage title="HCP Create Business Rules" entityType="hcp" actionType="create-business-rules" />} />
            <Route path="create-business-rules/:groupName" element={<ConfigWorkspacePage title="HCP Create Business Rules" entityType="hcp" actionType="create-business-rules" />} />
            <Route path="create-cleanser-function" element={<ConfigWorkspacePage title="HCP Create Cleanser Function" entityType="hcp" actionType="create-cleanser-function" />} />
            <Route path="create-data-modeler" element={<ConfigWorkspacePage title="HCP Create Data Modeler" entityType="hcp" actionType="create-data-modeler" />} />
            <Route path="create-match-rules" element={<ConfigWorkspacePage title="HCP Create Match Rules" entityType="hcp" actionType="create-match-rules" />} />
            <Route path="create-validation-function" element={<ConfigWorkspacePage title="HCP Create Validation Function" entityType="hcp" actionType="create-validation-function" />} />
            <Route path="create-rdm" element={<ConfigWorkspacePage title="HCP Create Reference Dataset" entityType="hcp" actionType="create-rdm" />} />
            <Route path="groups" element={<ConfigWorkspacePage title="HCP Groups" entityType="hcp" actionType="groups" />} />
            <Route path="edit-match-rules" element={<ConfigWorkspacePage title="HCP Edit Match Rules" entityType="hcp" actionType="edit-match-rules" />} />
            <Route path="rdm-groups" element={<ConfigWorkspacePage title="HCP RDM Groups" entityType="hcp" actionType="rdm-groups" />} />
            <Route path="business-group" element={<ConfigWorkspacePage title="HCP Business Group" entityType="hcp" actionType="business-group" />} />
            <Route path="business-group/:groupName" element={<ConfigWorkspacePage title="HCP Business Group" entityType="hcp" actionType="business-group" />} />
          </Route>
          <Route
            path="configuration/drug"
            element={
              <ProtectedRoute requiredPermission="admin">
                <ConfigurationTabsLayout basePath="/configuration/drug" entityLabel="Drug" />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="model" replace />} />
            <Route path="model" element={<DrugModelPage />} />
            <Route path="match-rules" element={<MatchRulesPage />} />
            <Route path="business-rules" element={<BusinessRulesPage />} />
            <Route path="validation-rules" element={<ValidationRulesPage />} />
            <Route path="cleansers" element={<DrugCleansersPage />} />
            <Route path="reference-datasets" element={<DrugReferenceDatasetsPage />} />
            <Route path="reference-datasets/workspace/:mode" element={<ConfigRdmWorkspacePage entityType="drug" />} />
            <Route path="reference-datasets/workspace/:mode/:itemKey" element={<ConfigRdmWorkspacePage entityType="drug" />} />
            <Route path="validation-rules/workspace/:mode" element={<ValidationWorkspacePage entityType="drug" />} />
            <Route path="validation-rules/workspace/:mode/:itemKey" element={<ValidationWorkspacePage entityType="drug" />} />
            <Route path="cleansers/workspace/:mode" element={<CleanserWorkspacePage entityType="drug" />} />
            <Route path="cleansers/workspace/:mode/:itemKey" element={<CleanserWorkspacePage entityType="drug" />} />
            <Route path=":section/workspace/:mode" element={<ConfigCrudWorkspacePage entityType="drug" />} />
            <Route path=":section/workspace/:mode/:itemKey" element={<ConfigCrudWorkspacePage entityType="drug" />} />
            <Route path="create-business-rules" element={<ConfigWorkspacePage title="Drug Create Business Rules" entityType="drug" actionType="create-business-rules" />} />
            <Route path="create-cleanser-function" element={<ConfigWorkspacePage title="Drug Create Cleanser Function" entityType="drug" actionType="create-cleanser-function" />} />
            <Route path="create-data-modeler" element={<ConfigWorkspacePage title="Drug Create Data Modeler" entityType="drug" actionType="create-data-modeler" />} />
            <Route path="create-match-rules" element={<ConfigWorkspacePage title="Drug Create Match Rules" entityType="drug" actionType="create-match-rules" />} />
            <Route path="create-validation-function" element={<ConfigWorkspacePage title="Drug Create Validation Function" entityType="drug" actionType="create-validation-function" />} />
            <Route path="create-rdm" element={<ConfigWorkspacePage title="Drug Create Reference Dataset" entityType="drug" actionType="create-rdm" />} />
            <Route path="groups" element={<ConfigWorkspacePage title="Drug Groups" entityType="drug" actionType="groups" />} />
            <Route path="edit-match-rules" element={<ConfigWorkspacePage title="Drug Edit Match Rules" entityType="drug" actionType="edit-match-rules" />} />
            <Route path="rdm-groups" element={<ConfigWorkspacePage title="Drug RDM Groups" entityType="drug" actionType="rdm-groups" />} />
            <Route path="business-group" element={<ConfigWorkspacePage title="Drug Business Group" entityType="drug" actionType="business-group" />} />
            <Route path="business-group/:groupName" element={<ConfigWorkspacePage title="Drug Business Group" entityType="drug" actionType="business-group" />} />
            <Route path="create-business-rules/:groupName" element={<ConfigWorkspacePage title="Drug Create Business Rules" entityType="drug" actionType="create-business-rules" />} />
          </Route>


          {/* User Pages */}
          <Route path="profile" element={<PlaceholderPage title="Profile" task="Future" />} />
          <Route path="settings" element={<PlaceholderPage title="Settings" task="Future" />} />

          {/* Error Pages */}
          <Route path="unauthorized" element={<UnauthorizedPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

    </ThemeProvider>
  );
};

export default App;
